import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import HeroSection from '../components/sections/HeroSection';
import ServicesSection from '../components/sections/ServicesSection';
import DifferentialsSection from '../components/sections/DifferentialsSection';
import ModelsSection from '../components/sections/ModelsSection';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import FAQSection from '../components/sections/FAQSection';
import ContactSection from '../components/sections/ContactSection';
import CTASection from '../components/sections/CTASection';
import LocationSection from '../components/sections/LocationSection';
import WhatsAppButton from '../components/common/WhatsAppButton';
import { brands, companyInfo } from '../data/mock';

const BrandPage = () => {
  const { slug } = useParams();
  const brand = brands.find(b => b.slug === slug);

  if (!brand) {
    return <Navigate to="/" replace />;
  }

  const customFaqs = [
    {
      question: `Quanto tempo demora o conserto de ${brand.name}?`,
      answer: `O tempo médio de reparo para ${brand.name} é de aproximadamente 2 horas, dependendo do serviço. Em casos mais complexos como reparo de placa, informamos o prazo no momento do orçamento.`
    },
    {
      question: `Vocês usam peças originais para ${brand.name}?`,
      answer: `Sim! Trabalhamos com peças originais e compatíveis de alta qualidade para ${brand.name}. Você pode escolher a opção que melhor atende suas necessidades e orçamento.`
    },
    {
      question: `Qual a garantia para conserto de ${brand.name}?`,
      answer: `Todos os serviços de reparo para ${brand.name} possuem garantia de 3 meses, tanto para a mão de obra quanto para as peças utilizadas.`
    },
    {
      question: `Vocês fazem orçamento gratuito para ${brand.name}?`,
      answer: `Sim! Realizamos orçamento gratuito e sem compromisso para qualquer modelo de ${brand.name}. Traga seu aparelho para análise ou entre em contato pelo WhatsApp.`
    },
    {
      question: `Quais modelos de ${brand.name} vocês consertam?`,
      answer: `Consertamos todos os modelos de ${brand.name}, desde os mais antigos até os lançamentos mais recentes. Nossa equipe é especializada e possui as ferramentas necessárias para cada modelo.`
    },
    {
      question: "Onde fica a assistência técnica?",
      answer: `Estamos localizados na ${companyInfo.fullAddress}. Funcionamos de segunda a sexta-feira, das ${companyInfo.hours}.`
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Tsw Info - Assistência Técnica ${brand.name} em Osasco`,
    "description": brand.metaDescription,
    "image": companyInfo.logo,
    "telephone": `+55${companyInfo.whatsapp}`,
    "email": companyInfo.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": companyInfo.address,
      "addressLocality": companyInfo.city,
      "addressRegion": companyInfo.state,
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-23.5324",
      "longitude": "-46.7917"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "10:00",
      "closes": "18:00"
    },
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "89"
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": `Assistência Técnica ${brand.name}`,
        "description": `Conserto e reparo de ${brand.name} em Osasco`
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{brand.metaTitle}</title>
        <meta name="description" content={brand.metaDescription} />
        <meta name="keywords" content={`assistência técnica ${brand.name.toLowerCase()} osasco, conserto ${brand.name.toLowerCase()} osasco, reparo ${brand.name.toLowerCase()} osasco, troca tela ${brand.name.toLowerCase()} osasco`} />
        <meta property="og:title" content={brand.metaTitle} />
        <meta property="og:description" content={brand.metaDescription} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://tswinfo.com.br/${brand.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Header />
      
      <main>
        <HeroSection 
          title={brand.heroTitle}
          subtitle={brand.heroSubtitle}
          brandColor={brand.color}
        />
        <ServicesSection brandName={brand.name} />
        <ModelsSection brandName={brand.name} models={brand.models} />
        <DifferentialsSection />
        <CTASection 
          title={`Precisa consertar seu ${brand.name}?`}
          subtitle={`Entre em contato agora e receba um orçamento gratuito para seu ${brand.name}. Atendimento em até 2 horas!`}
        />
        <TestimonialsSection />
        <LocationSection />
        <FAQSection customFaqs={customFaqs} />
        <ContactSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default BrandPage;